/**
 * DESIGN.md: status in a data row is a leading colour dot + label text —
 * never colour alone (also the WCAG 1.4.1 requirement), and explicitly not a
 * wash-filled `rounded-full` chip. `Pill` (`components/ui/Pill.tsx`) is the
 * chip form; it stays reserved for floating chrome (badges, the session
 * drawer's live status) — this is the plain-text idiom for a table/list row.
 * The dot is decorative (`aria-hidden`), so a screen reader reads the label
 * once, not "dot, label".
 *
 * 2026-08-19 remediation Slice D, Task 3 — replaces the wash-filled `Pill`
 * `ResultsTable.tsx`'s `StatusCell` and `RegistruCredite.tsx` had re-wrapped
 * the correct dot+label form back into.
 */
export type StatusTone = 'ok' | 'warn' | 'bad' | 'mute' | 'teal';

const DOT: Record<StatusTone, string> = {
  ok: 'bg-ok',
  warn: 'bg-warn',
  bad: 'bg-bad',
  mute: 'bg-mute',
  teal: 'bg-teal',
};

export function StatusLabel({
  tone,
  label,
  pulse = false,
  className = '',
}: {
  tone: StatusTone;
  label: string;
  /** Adds a live/in-progress pulse to the dot (`motion-safe:animate-pulse`)
   *  — e.g. a run actively processing. Off by default: most statuses are
   *  static, and the dot's colour (`tone`) already carries the meaning; this
   *  is purely an extra liveness cue for the one status that needs it. */
  pulse?: boolean;
  className?: string;
}) {
  return (
    <span className={`inline-flex items-center gap-2 text-[13.5px] text-ink-2 ${className}`}>
      <span
        aria-hidden="true"
        className={`size-1.5 shrink-0 rounded-full ${DOT[tone]} ${pulse ? 'motion-safe:animate-pulse' : ''}`}
      />
      {label}
    </span>
  );
}
